import polytechnicInstitutions from '../data/dcece-polytechnic/DCECE_Polytechnic_Institutions_2025.json';
import { getExamVariantOptions, getExamVariantRows } from './examCutoffModules.js';
import { getExamSeatMatrixModule } from './examSeatMatrixModules.js';

const normalizeText = (value = '') => String(value).replace(/\s+/g, ' ').trim();

const slugify = (value = '') =>
  normalizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const compactText = (value = '') => normalizeText(value).toLowerCase().replace(/[^a-z0-9]+/g, '');

const commonInstitutionWords = new Set([
  'college',
  'hospital',
  'institute',
  'institutes',
  'medical',
  'school',
  'training',
  'research',
  'centre',
  'center',
  'campus',
  'admission',
  'paramedical',
  'nursing',
  'science',
  'sciences',
  'engineering',
  'polytechnic',
  'ayurved',
  'ayurveda',
  'ayurvedic',
  'homoeopathic',
  'homeopathic',
  'homoeo',
  'homoeo',
  'unani',
  'and',
  'of',
  'the',
  'road',
  'sadar',
  'subdivisional',
  'subdivision',
  'sub',
  'old',
  'referral',
  'phc',
  'chc',
  'ward',
  'no',
]);

const tokenizeInstitution = (value = '') =>
  normalizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .split(/\s+/)
    .filter((token) => token && !commonInstitutionWords.has(token) && token.length > 1);

const institutionAliasMap = {
  ugmacAyush: {
    [compactText('Govt. Tibbi College, Patna')]: [compactText('GOVT.TIBBI COLLEGE, PATNA')],
    [compactText('Dr. yaduvir Sinha Homoeo. Medical College and Hospital Darbhanga')]: [
      compactText('DR. YADUBIR SINHA HOMOEOPATHIC MEDICAL COLLEGE AND HOSPITAL DARBHANGA'),
    ],
    [compactText('G.D. Memorial Medical College & Hosptial, Patna')]: [
      compactText(
        'GD MEMORIAL HOMOEOPATHIC MEDICAL COLLEGE & HOSPITAL (EAST RAMKRISHNA NAGAR, NEAR NEW BYPASS KANKARBAGH, PATNA-800027)',
      ),
    ],
    [compactText('Niteshwar Ayurvedic College, Muzaffarpur')]: [
      compactText('NITISHWAR AYURVED MEDICAL COLLEGE & HOSPITAL, MUZAFFARPUR'),
    ],
    [compactText('Shree.M.S.Jageswari, Ayurved College & Hospital, Chapra')]: [
      compactText('SRI MOTI SINGH JAGESHWARI AYURVED COLLEGE & HOSPITAL, CHAPRA'),
    ],
    [compactText('Swami Raghavendra Tridandi Ayurveda College, Gaya')]: [
      compactText(
        'SWAMI RAGHWENDRACHARYA TRIDANDI AYURVEDA MHAVIDYALAYA AND CHIKITSALAYA,KARJARA STATION, GAYA',
      ),
    ],
    [compactText('Z.H.UNANI MEDICAL COLLEGE & HOSPITAL, SIWAN')]: [
      compactText('ZULFEQUAR HAIDER UNANI MEDICAL COLLEGE AND HOSPITAL, SIWAN'),
    ],
    [compactText('Patna Homoeopathic Medical College & Hospital , Patna')]: [
      compactText('PATNA HOMOEOPATHIC MEDICAL COLLEGE & HOSPITAL , PATNA'),
    ],
    [compactText('Maharshi Mehi Homeopathic College & Hospital Katihar')]: [
      compactText('MAHARSHI MENHI HOMOEO MEDICAL COLLEGE AND HOSPITAL KATIHAR'),
    ],
    [compactText('Mangla Kamla Homeopathic Medical College & Hospital, Siwan')]: [
      compactText('MANGLA KAMLA HOMOEOPATHIC MEDICAL COLLEGE AND HOSPITAL SRI NAGAR, SIWAN'),
    ],
    [compactText('Nezamia Unani Medical College & Hospital, Gaya')]: [
      compactText('NEZAMIA UNANI MEDICAL COLLEGE & HOSPITAL, GAYA'),
    ],
    [compactText('Kent Homeopathic Medical College and Hospital, Vaishali')]: [
      compactText('KENT HOMEOPATHIC MEDICAL COLLEGE AND HOSPITAL, VAISHALI'),
    ],
  },
};

const directoryConfigs = {
  bceceEngineering: {
    key: 'bceceEngineering',
    entityLabel: 'College',
    entityPluralLabel: 'Colleges',
    courseLabel: 'Branch',
    coursesLabel: 'Available Branches',
    typeLabel: 'College Type',
    listPageLabel: 'BCECE Engineering College Directory',
    heroTitleLead: 'Find the Right BCECE',
    heroTitleSecond: 'Engineering College for Your Future.',
    heroTitleAccent: 'All in One Place.',
    heroDescription:
      'Explore remaining-seat engineering colleges connected to the BCECE Engineering 2025 seat matrix and cutoff datasets.',
    directoryPath: '/bcece-engineering-colleges',
    predictorPath: '/bcece-engineering-predictor',
    dataPath: '/bcece-engineering-cutoff-seat-matrix',
    dataSource: 'BCECE Engineering 2025 seat matrix and cutoff files',
    themeLabel: 'Engineering',
  },
  paramedical: {
    key: 'paramedical',
    entityLabel: 'Institute',
    entityPluralLabel: 'Institutes',
    courseLabel: 'Course',
    coursesLabel: 'Available Courses',
    typeLabel: 'Institute Type',
    listPageLabel: 'Bihar Paramedical Institute Directory',
    heroTitleLead: 'Find the Right Paramedical',
    heroTitleSecond: 'Institute for Your Future.',
    heroTitleAccent: 'All in One Place.',
    heroDescription:
      'Browse the PM / PMM institute directory built from the uploaded combined seat matrix and cutoff level files.',
    directoryPath: '/paramedical-colleges',
    predictorPath: '/paramedical-predictor',
    dataPath: '/paramedical-cutoff-seat-matrix',
    dataSource: 'DCECE PM / PMM seat matrix and cutoff files',
    themeLabel: 'Paramedical',
  },
  polytechnic: {
    key: 'polytechnic',
    entityLabel: 'Institute',
    entityPluralLabel: 'Institutes',
    courseLabel: 'Branch',
    coursesLabel: 'Available Branches',
    typeLabel: 'Institute Type',
    listPageLabel: 'Bihar Polytechnic Institute Directory',
    heroTitleLead: 'Find the Right Polytechnic',
    heroTitleSecond: 'Institute for Your Future.',
    heroTitleAccent: 'All in One Place.',
    heroDescription:
      'Compare government and private polytechnic institutes using the uploaded DCECE PE seat matrix and cutoff data.',
    directoryPath: '/polytechnic-colleges',
    predictorPath: '/polytechnic-predictor',
    dataPath: '/polytechnic-cutoff-seat-matrix',
    dataSource: 'DCECE Polytechnic 2025 seat matrix workbook and cutoff file',
    themeLabel: 'Polytechnic',
  },
  ugmacAyush: {
    key: 'ugmacAyush',
    entityLabel: 'College',
    entityPluralLabel: 'Colleges',
    courseLabel: 'Course',
    coursesLabel: 'Available Courses',
    typeLabel: 'College Type',
    listPageLabel: 'Bihar Medical (UGMAC Ayush) College Directory',
    heroTitleLead: 'Find the Right AYUSH',
    heroTitleSecond: 'College for Your Future.',
    heroTitleAccent: 'All in One Place.',
    heroDescription:
      'Explore AYUSH colleges through the connected UGMAC Ayush seat matrix and derived opening-closing rank flow.',
    directoryPath: '/ugmac-ayush-colleges',
    predictorPath: '/ugmac-ayush-predictor',
    dataPath: '/ugmac-ayush-cutoff-seat-matrix',
    dataSource: 'UGMAC Ayush seat matrix and derived 2025 cutoff file',
    themeLabel: 'Ayush',
  },
};

const getFallbackInstituteType = (moduleKey) => {
  if (moduleKey === 'paramedical') return 'Not tagged in source';
  return 'Not specified';
};

const getDisplayInstituteType = (moduleKey, instituteType) =>
  instituteType || getFallbackInstituteType(moduleKey);

const getCourseFamilies = (moduleKey, courses) => {
  if (moduleKey === 'ugmacAyush') {
    const families = new Set();
    courses.forEach((course) => {
      if (course.includes('B.A.M.S')) families.add('Ayurveda');
      if (course.includes('B.H.M.S')) families.add('Homeopathy');
      if (course.includes('B.U.M.S')) families.add('Unani');
    });
    return [...families];
  }

  if (moduleKey === 'polytechnic') {
    return ['Diploma'];
  }

  if (moduleKey === 'bceceEngineering') {
    return ['Engineering'];
  }

  if (moduleKey === 'paramedical') {
    return ['Paramedical'];
  }

  return [];
};

const isAliasMatch = (moduleKey, seatInstitution, cutoffInstitution) => {
  const aliases = institutionAliasMap[moduleKey]?.[compactText(seatInstitution)] ?? [];
  return aliases.includes(compactText(cutoffInstitution));
};

const getInstitutionSimilarityScore = (moduleKey, seatInstitution, cutoffInstitution) => {
  if (!seatInstitution || !cutoffInstitution) return 0;

  const seatCompact = compactText(seatInstitution);
  const cutoffCompact = compactText(cutoffInstitution);

  if (seatCompact === cutoffCompact || isAliasMatch(moduleKey, seatInstitution, cutoffInstitution)) {
    return 1;
  }

  if (
    seatCompact.length > 10 &&
    cutoffCompact.length > 10 &&
    (seatCompact.includes(cutoffCompact) || cutoffCompact.includes(seatCompact))
  ) {
    return 0.95;
  }

  const seatTokens = tokenizeInstitution(seatInstitution);
  const cutoffTokens = tokenizeInstitution(cutoffInstitution);

  if (seatTokens.length === 0 || cutoffTokens.length === 0) {
    return 0;
  }

  const overlap = seatTokens.filter((token) => cutoffTokens.includes(token));
  if (overlap.length === 0) {
    return 0;
  }

  const minCoverage = overlap.length / Math.min(seatTokens.length, cutoffTokens.length);
  const maxCoverage = overlap.length / Math.max(seatTokens.length, cutoffTokens.length);
  const score = minCoverage * 0.8 + maxCoverage * 0.2;

  return score >= 0.72 && overlap.length >= 2 ? score : 0;
};

const getSeatRowsForModule = (moduleKey) => getExamSeatMatrixModule(moduleKey)?.data ?? [];

const getAllCutoffRowsForModule = (moduleKey) => {
  const variants = getExamVariantOptions(moduleKey);
  if (variants.length === 0) {
    return getExamVariantRows(moduleKey, 'default');
  }

  return variants.flatMap((variant) =>
    getExamVariantRows(moduleKey, variant.key).map((row) => ({
      ...row,
      _variantKey: variant.key,
      _variantLabel: variant.label,
    })),
  );
};

const groupSeatRowsByInstitution = (moduleKey) => {
  const seatRows = getSeatRowsForModule(moduleKey);
  const grouped = new Map();

  seatRows.forEach((row) => {
    const institution = normalizeText(row.institution);
    const current = grouped.get(institution) ?? {
      name: institution,
      slug: slugify(institution),
      instituteTypes: new Set(),
      courses: new Set(),
      courseFamilies: new Set(),
      totalSeats: 0,
      seatRowCount: 0,
    };

    if (row.instituteType) {
      current.instituteTypes.add(row.instituteType);
    }
    current.courses.add(normalizeText(row.course));
    getCourseFamilies(moduleKey, [normalizeText(row.course)]).forEach((family) =>
      current.courseFamilies.add(family),
    );
    current.totalSeats += Number(row.totalSeats ?? 0) || 0;
    current.seatRowCount += 1;
    grouped.set(institution, current);
  });

  return grouped;
};

export const getExamDirectoryConfig = (moduleKey) => directoryConfigs[moduleKey] ?? null;

export const getExamDirectoryEntries = (moduleKey) => {
  const config = getExamDirectoryConfig(moduleKey);
  const seatModule = getExamSeatMatrixModule(moduleKey);

  if (!config || !seatModule) {
    return [];
  }

  const grouped = groupSeatRowsByInstitution(moduleKey);
  const cutoffRows = getAllCutoffRowsForModule(moduleKey);

  return [...grouped.values()]
    .map((entry) => {
      const exactCutoffRows = cutoffRows.filter(
        (row) => compactText(row.institute) === compactText(entry.name) || isAliasMatch(moduleKey, entry.name, row.institute),
      );

      const fuzzyCutoffRows =
        exactCutoffRows.length > 0
          ? exactCutoffRows
          : cutoffRows.filter((row) => getInstitutionSimilarityScore(moduleKey, entry.name, row.institute) > 0);

      return {
        slug: entry.slug,
        name: entry.name,
        instituteType: getDisplayInstituteType(
          moduleKey,
          entry.instituteTypes.size === 1 ? [...entry.instituteTypes][0] : [...entry.instituteTypes][0] || '',
        ),
        courses: [...entry.courses].sort((a, b) => a.localeCompare(b)),
        courseFamilies: [...entry.courseFamilies],
        totalSeats: entry.totalSeats,
        seatRowCount: entry.seatRowCount,
        cutoffRowCount: fuzzyCutoffRows.length,
      };
    })
    .sort((first, second) => first.name.localeCompare(second.name));
};

export const getExamDirectoryEntryBySlug = (moduleKey, slug) =>
  getExamDirectoryEntries(moduleKey).find((entry) => entry.slug === slug) ?? null;

export const getExamDirectorySlugByInstitution = (moduleKey, institutionName) => {
  const normalizedInstitution = normalizeText(institutionName);
  if (!normalizedInstitution) {
    return null;
  }

  const entries = getExamDirectoryEntries(moduleKey);
  const exactMatch = entries.find(
    (entry) =>
      compactText(entry.name) === compactText(normalizedInstitution) ||
      isAliasMatch(moduleKey, entry.name, normalizedInstitution),
  );

  if (exactMatch) {
    return exactMatch.slug;
  }

  const fuzzyMatch = entries
    .map((entry) => ({
      slug: entry.slug,
      score: getInstitutionSimilarityScore(moduleKey, entry.name, normalizedInstitution),
    }))
    .filter((entry) => entry.score > 0)
    .sort((first, second) => second.score - first.score)[0];

  return fuzzyMatch?.slug ?? null;
};

export const getExamSeatRowsForInstitution = (moduleKey, institution) => {
  const institutionCompact = compactText(institution);
  return getSeatRowsForModule(moduleKey)
    .filter((row) => compactText(row.institution) === institutionCompact)
    .sort((first, second) => normalizeText(first.course).localeCompare(normalizeText(second.course)));
};

export const getMatchedCutoffRowsForInstitution = (moduleKey, institution, variantKey = 'default') => {
  const rows = getExamVariantRows(moduleKey, variantKey);
  const exactMatches = rows.filter(
    (row) => compactText(row.institute) === compactText(institution) || isAliasMatch(moduleKey, institution, row.institute),
  );

  if (exactMatches.length > 0) {
    return exactMatches;
  }

  return rows.filter((row) => getInstitutionSimilarityScore(moduleKey, institution, row.institute) > 0);
};

export const getExamInstitutionTypeOptions = (moduleKey) => {
  const entries = getExamDirectoryEntries(moduleKey);
  const options = [...new Set(entries.map((entry) => entry.instituteType).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b),
  );
  return options;
};

export const getExamInstitutionCourseOptions = (moduleKey) => {
  const entries = getExamDirectoryEntries(moduleKey);
  return [...new Set(entries.flatMap((entry) => entry.courses))].sort((a, b) => a.localeCompare(b));
};

export const getExamInstitutionListOptions = (moduleKey) =>
  getExamDirectoryEntries(moduleKey).map((entry) => entry.name);

export const filterExamDirectoryEntries = (
  moduleKey,
  {
    instituteType = 'All',
    institution = 'All',
    course = 'All',
  } = {},
) =>
  getExamDirectoryEntries(moduleKey)
    .filter((entry) => instituteType === 'All' || entry.instituteType === instituteType)
    .filter((entry) => institution === 'All' || entry.name === institution)
    .map((entry) => ({
      ...entry,
      courses: course === 'All' ? entry.courses : entry.courses.filter((item) => item === course),
    }))
    .filter((entry) => entry.courses.length > 0);

export const getExamDirectoryMeta = (moduleKey) => {
  if (moduleKey === 'polytechnic') {
    return {
      institutionCount: polytechnicInstitutions.length,
      sourceInstitutionCount: getExamDirectoryEntries(moduleKey).length,
    };
  }

  return {
    institutionCount: getExamDirectoryEntries(moduleKey).length,
    sourceInstitutionCount: getExamDirectoryEntries(moduleKey).length,
  };
};
